import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { api } from '../api';
import {
  NEWS_API_URL,
  ACADEMY_API_URL,
  ANNOUNCEMENTS_API_PATH,
  ANNOUNCEMENTS_PAGE_SIZE
} from '@/constants/newsAndAcademy';
import {
  NewsResponse,
  ArticlesResponse,
  AnnouncementsEnvelope,
  AnnouncementsResponse,
  ListAnnouncementsParams
} from './types';

/**
 * News & Academy endpoints — injected into the existing `api` slice.
 *
 * WHY injectEndpoints + queryFn instead of a separate createApi:
 *
 * 1. No new reducer to register in the store.
 * 2. No new middleware to add — the existing api.middleware already handles
 *    caching, invalidation, and polling for these endpoints too.
 * 3. The store's existing serializableCheck config covers all api/* actions.
 *
 * WHY queryFn instead of a query string:
 *
 * The main api slice has a baseQuery pointing at the Rabex API origin.
 * News and academy live on external WordPress sites (rabex.ir/news,
 * rabex.ir/academy). If we used a plain `query` string, the baseQuery would
 * prepend the wrong base URL AND potentially attach Authorization headers to
 * an external WordPress server — both wrong.
 *
 * queryFn bypasses baseQuery entirely and does a plain fetch to the absolute
 * URL with no auth headers. The return shape { data } / { error } is what
 * RTK Query expects from any queryFn.
 *
 * CACHE: every endpoint below sets keepUnusedDataFor: 300 (5 minutes) itself, over the
 * root api slice's 60s default — news content doesn't change by the second.
 */

// A hanging rabex.ir request would otherwise keep the skeleton up indefinitely.
const FEED_TIMEOUT_MS = 10_000;

/**
 * GET one WordPress feed, no auth headers. Aborts when RTK cancels the query (`signal`) and
 * gives up after FEED_TIMEOUT_MS, reporting a TIMEOUT_ERROR the screen shows like any other.
 */
const fetchFeed = async <T,>(
  url: string,
  signal: AbortSignal
): Promise<{ data: T } | { error: FetchBaseQueryError }> => {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, FEED_TIMEOUT_MS);
  const cancel = () => controller.abort();
  signal.addEventListener('abort', cancel);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      return { error: { status: response.status, data: response.statusText } };
    }
    return { data: (await response.json()) as T };
  } catch (error) {
    return {
      error: {
        status: timedOut ? 'TIMEOUT_ERROR' : 'FETCH_ERROR',
        error: String(error)
      }
    };
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', cancel);
  }
};
const newsAndAcademyApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetch latest news posts.
     * GET https://rabex.ir/news/wp-json/rabex/v1/latest-news
     */
    getLatestNews: builder.query<NewsResponse, void>({
      keepUnusedDataFor: 300, // 5 minutes — news doesn't change by the second
      queryFn: (_arg, { signal }) =>
        fetchFeed<NewsResponse>(NEWS_API_URL, signal)
    }),

    /**
     * Fetch latest academy articles.
     * GET https://rabex.ir/academy/wp-json/rabex/v1/latest-articles
     */
    getLatestArticles: builder.query<ArticlesResponse, void>({
      keepUnusedDataFor: 300, // 5 minutes
      queryFn: (_arg, { signal }) =>
        fetchFeed<ArticlesResponse>(ACADEMY_API_URL, signal)
    }),
    /**
     * Fetch one page of published announcements, newest first.
     * GET /api/v1/rtc/announcements?page=&limit=
     *
     * WHY `query` and not `queryFn` like the two above: announcements are ours,
     * served by rtc on the same origin under /api/v1. Going through baseQuery
     * is what we want here — auth header, token refresh and the shared error
     * handling all apply. Only the external WordPress feeds need the bypass.
     *
     * PAGINATION: serializeQueryArgs collapses every page into ONE cache entry
     * and `merge` appends to it, so the "load more" button grows a single list
     * instead of replacing it. forceRefetch is what makes a new page actually
     * hit the network despite the shared cache key.
     */
    getLatestAnnouncements: builder.query<
      AnnouncementsResponse,
      ListAnnouncementsParams
    >({
      keepUnusedDataFor: 300, // 5 minutes
      query: ({ page, limit }) => ({
        url: ANNOUNCEMENTS_API_PATH,
        method: 'GET',
        params: {
          page: page ?? 1,
          limit: limit ?? ANNOUNCEMENTS_PAGE_SIZE
        }
      }),

      /*
       * Unwrap the {"data": ...} envelope and fill in what proto3 JSON drops:
       * an empty feed comes back as {"data":{}}, so an absent `announcements`
       * means [] and an absent `total` means 0 — not an error.
       */
      transformResponse: (
        response: AnnouncementsEnvelope,
        _meta,
        arg
      ): AnnouncementsResponse => ({
        announcements: response?.data?.announcements ?? [],
        total: response?.data?.total ?? 0,
        page: response?.data?.page ?? arg?.page ?? 1,
        limit: response?.data?.limit ?? arg?.limit ?? ANNOUNCEMENTS_PAGE_SIZE
      }),

      // One cache entry for the whole feed, regardless of which page was asked for.
      serializeQueryArgs: ({ endpointName }) => endpointName,

      /*
       * `currentCache` is an immer draft, so this mutates rather than returns.
       * eslint-disable-next-line no-param-reassign is the same allowance every
       * other draft-mutating reducer in the app uses.
       */
      /* eslint-disable no-param-reassign */
      merge: (currentCache, newItems) => {
        if (newItems.page <= 1) {
          // Page 1 is a fresh start (first load, retry, refetch) — replace,
          // don't append, or a retry would duplicate what is already on screen.
          currentCache.announcements = newItems.announcements;
        } else {
          // Guard against a double-fire appending the same slugs twice.
          const seen = new Set(currentCache.announcements.map((a) => a.slug));
          currentCache.announcements.push(
            ...newItems.announcements.filter((a) => !seen.has(a.slug))
          );
        }

        currentCache.total = newItems.total;
        currentCache.page = newItems.page;
        currentCache.limit = newItems.limit;
      },
      /* eslint-enable no-param-reassign */

      // Without this the shared cache key would make page 2 look already-fetched.
      forceRefetch: ({ currentArg, previousArg }) =>
        (currentArg?.page ?? 1) !== (previousArg?.page ?? 1)
    })
  }),

  // overrideExisting: false means if these endpoint names already exist in the
  // slice (e.g. from a hot-reload cycle), don't re-register them.
  overrideExisting: false
});

export const {
  useGetLatestNewsQuery,
  useGetLatestArticlesQuery,
  useGetLatestAnnouncementsQuery
} = newsAndAcademyApi;
